import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiX } = FiIcons;

function SnowGlobe({ uploadedImage, onImageUpload, language }) {
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [processedImage, setProcessedImage] = useState(null);

  const translations = {
    fr: { upload: 'Télécharger une image', remove: 'Supprimer' },
    en: { upload: 'Upload Image', remove: 'Remove' },
    es: { upload: 'Subir Imagen', remove: 'Eliminar' },
    de: { upload: 'Bild hochladen', remove: 'Entfernen' }
  };

  const t = translations[language] || translations.en;

  // Process image to add transparency around edges
  const processImageForSnowGlobe = (imgFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Set canvas size to match image
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Create radial gradient mask for transparency
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const maxRadius = Math.min(canvas.width, canvas.height) / 2;

        for (let i = 0; i < data.length; i += 4) {
          const pixelIndex = i / 4;
          const x = pixelIndex % canvas.width;
          const y = Math.floor(pixelIndex / canvas.width);

          // Calculate distance from center
          const dx = x - centerX;
          const dy = y - centerY;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // Create smooth falloff
          const falloff = Math.max(0, 1 - (distance / maxRadius) * 1.2);
          data[i + 3] = Math.floor(data[i + 3] * falloff);
        }

        ctx.putImageData(imageData, 0, 0);
        const processedDataUrl = canvas.toDataURL('image/png');
        setProcessedImage(processedDataUrl);
        onImageUpload(processedDataUrl);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(imgFile);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processImageForSnowGlobe(file);
    }
  };

  const handleRemoveImage = () => {
    setProcessedImage(null);
    onImageUpload(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Snow Globe Container */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-48 h-56 rounded-full bg-gradient-to-br from-blue-200 via-blue-100 to-blue-50 shadow-2xl border-8 border-white/30 overflow-hidden"
      >
        {/* Inner glow */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-t from-transparent to-white/20" />

        {/* Base */}
        <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-40 h-8 bg-gradient-to-b from-amber-700 to-amber-900 rounded-full shadow-lg" />

        {/* Uploaded Image */}
        {processedImage && (
          <motion.img
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            src={processedImage}
            alt="Snow Globe"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}

        {/* Empty State */}
        {!processedImage && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-blue-400/50">
            <div className="text-4xl mb-2">❄️</div>
            <div className="text-xs text-center px-4">Téléchargez une image</div>
          </div>
        )}

        {/* Shine effect */}
        <motion.div
          animate={{ opacity: [0.3, 0.8, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="absolute top-4 left-4 w-8 h-8 bg-white rounded-full blur-md"
        />
      </motion.div>

      {/* Hidden Canvas for processing */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Controls */}
      <div className="flex gap-2">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all"
        >
          <SafeIcon icon={FiUpload} className="text-lg" />
          {t.upload}
        </motion.button>

        {processedImage && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleRemoveImage}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all"
          >
            <SafeIcon icon={FiX} className="text-lg" />
            {t.remove}
          </motion.button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageSelect}
        className="hidden"
      />
    </div>
  );
}

export default SnowGlobe;